import React from 'react';
import { FileText, Shield, Lock, Users } from 'lucide-react';

export default function Terms() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <FileText className="mx-auto mb-4" size={64} />
          <h1 className="text-4xl font-bold mb-4">Terms and Conditions</h1>
          <p className="text-lg text-blue-100">Last Updated: February 14, 2026</p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8 space-y-8">
          
          {/* Key Points */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pb-8 border-b">
            <div className="text-center">
              <Shield className="mx-auto text-blue-600 mb-2" size={40} />
              <h3 className="font-semibold text-gray-800 mb-1">Your Privacy</h3>
              <p className="text-sm text-gray-600">Data protected with encryption</p>
            </div>
            <div className="text-center">
              <Lock className="mx-auto text-green-600 mb-2" size={40} />
              <h3 className="font-semibold text-gray-800 mb-1">Secure Platform</h3>
              <p className="text-sm text-gray-600">Industry-standard security</p>
            </div>
            <div className="text-center">
              <Users className="mx-auto text-purple-600 mb-2" size={40} />
              <h3 className="font-semibold text-gray-800 mb-1">Fair Use</h3>
              <p className="text-sm text-gray-600">Equal opportunities for all</p>
            </div>
          </div>

          {/* Section 1 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">1. Acceptance of Terms</h2>
            <p className="text-gray-700 leading-relaxed">
              By accessing and using the Talent Link Employment Agency platform, you agree to be bound by these Terms and Conditions. 
              If you do not agree to these Terms, please do not use the Platform.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">2. User Accounts</h2>
            <div className="space-y-3 text-gray-700">
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">2.1 Registration</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>Users must provide accurate and complete information</li>
                  <li>Users are responsible for maintaining account confidentiality</li>
                  <li>Users must be at least 18 years old</li>
                  <li>One person may not maintain multiple accounts</li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold text-gray-800 mb-2">2.2 Account Security</h3>
                <ul className="list-disc list-inside space-y-1 ml-4">
                  <li>You are responsible for all activities under your account</li>
                  <li>Notify us immediately of any unauthorized use</li>
                  <li>We reserve the right to suspend or terminate violating accounts</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">3. Data Protection and Privacy</h2>
            <div className="bg-blue-50 border-l-4 border-blue-600 p-4 mb-4">
              <p className="text-sm text-blue-900">
                <strong>Your Privacy Matters:</strong> We implement appropriate technical and organizational measures to protect your personal data in accordance with applicable data protection laws.
              </p>
            </div>
            <div className="space-y-3 text-gray-700">
              <p><strong>Data Usage:</strong> Your data will be used for recruitment purposes, matching candidates with employers. Employers may view candidate profiles for positions you have applied to.</p>
              <p><strong>Data Retention:</strong> We retain your data as long as your account is active or as needed to provide services. You may request deletion at any time.</p>
              <p><strong className="text-red-600">Important:</strong> We will never sell your personal data to third parties.</p>
            </div>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">4. User Conduct</h2>
            <div className="space-y-3 text-gray-700">
              <p className="font-semibold">Users shall NOT:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Provide false, misleading, or fraudulent information</li>
                <li>Impersonate any person or entity</li>
                <li>Use the Platform for illegal purposes</li>
                <li>Upload malicious code or harmful content</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Scrape or collect data without authorization</li>
                <li>Send spam or unsolicited communications</li>
              </ul>
            </div>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">5. Services for Job Seekers</h2>
            <div className="bg-green-50 border-l-4 border-green-600 p-4 mb-4">
              <p className="text-sm text-green-900">
                <strong>Free Services:</strong> Job search and application services are provided FREE of charge to job seekers. We do not charge job seekers for placements or introductions to employers.
              </p>
            </div>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Job seekers may apply to multiple positions</li>
              <li>Applications are subject to employer review and selection</li>
              <li>We do not guarantee interviews or employment</li>
              <li>Background checks and reference verification may be conducted</li>
            </ul>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">6. Services for Employers</h2>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Job postings must be accurate and comply with labor laws</li>
              <li>Employers may only use candidate information for recruitment purposes</li>
              <li>Candidate data must be handled per data protection laws</li>
              <li>Service fees are as agreed in separate service agreements</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">7. Compliance with Laws</h2>
            <div className="space-y-3 text-gray-700">
              <p><strong>Labor Laws:</strong> All activities must comply with Zimbabwe labor laws and regulations. We operate under license from the Ministry of Labour.</p>
              <p><strong>Anti-Discrimination:</strong> We prohibit discrimination based on race, gender, age, religion, disability, or other protected characteristics. All users must adhere to equal opportunity principles.</p>
            </div>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">8. Limitation of Liability</h2>
            <p className="text-gray-700">
              To the maximum extent permitted by law, we are not liable for indirect, incidental, or consequential damages. 
              We do not guarantee employment or hiring outcomes. Final hiring decisions rest with employers.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-3">9. Termination</h2>
            <p className="text-gray-700 mb-2">You may terminate your account at any time through your account settings.</p>
            <p className="text-gray-700">We may suspend or terminate accounts for violations of these Terms. We reserve the right to refuse service to anyone.</p>
          </section>

          {/* Contact */}
          <section className="bg-gray-50 border border-gray-200 rounded-lg p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-3">Contact Us</h2>
            <p className="text-gray-700 mb-3">For questions about these Terms, contact us:</p>
            <div className="space-y-1 text-gray-700">
              <p><strong>Talent Link Employment Agency</strong></p>
              <p>Email: <a href="mailto:info@talentlinkemploymentagency.com" className="text-blue-600 hover:underline">info@talentlinkemploymentagency.com</a></p>
              <p>Phone: <a href="tel:+263772155436" className="text-blue-600 hover:underline">+263 77 215 5436</a></p>
              <p>Location: Harare, Zimbabwe</p>
            </div>
          </section>

          {/* Acknowledgment */}
          <div className="bg-blue-600 text-white rounded-lg p-6 text-center">
            <p className="text-lg font-semibold mb-2">Acknowledgment</p>
            <p className="text-blue-100">
              By using the Platform, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions.
            </p>
          </div>

          <p className="text-center text-sm text-gray-500">
            © 2026 Talent Link Employment Agency. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
}
